import { useEffect, useRef, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {ArrowLeft, TreePine, Upload, X, Loader2, Check, ChevronRight, ChevronLeft, AlertCircle, MapPin, AlertTriangle, UserPlus, ShieldCheck, User, } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { gerarProtocolo, formatarCPF, formatarTelefone } from '@/utils/protocolo'
import { normalizarCPF, senhaDosCPF } from '@/utils/cidadaoAuth'
import type { EstadoArvore } from '@/types'
import toast from 'react-hot-toast'

const ESTADOS_ARVORE: EstadoArvore[] = [
  'Saudável', 'Doente', 'Morta', 'Risco de Queda', 'Danificando Estrutura',
]

const STEPS = ['Solicitante', 'Árvore & Fotos', 'Termo', 'Confirmar']

interface Props {
  editData?: any
  onSaved?: () => void
  onCancel?: () => void
}

function formatarCEP(v: string) {
  return v.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
}

// ─── Estado da conta cidadão para este CPF ───────────────────────────────────
type EstadoCidadao = 'idle' | 'verificando' | 'sem_acesso' | 'ja_tem_acesso' | 'criando' | 'criado' | 'erro'

export default function NovaSolicitacao({ editData, onSaved }: Props) {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [fotos, setFotos] = useState<string[]>([])
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [buscandoCEP, setBuscandoCEP] = useState(false)
  const fotoInputRef = useRef<HTMLInputElement>(null)

  // ─── Conta cidadão ────────────────────────────────────────────────────────
  const [estadoCidadao, setEstadoCidadao] = useState<EstadoCidadao>('idle')
  const [erroCidadao, setErroCidadao] = useState('')

  const [form, setForm] = useState({
    nomeCompleto: '',
    cpf: '',
    telefone: '',
    ruaNome: '',
    numero: '',
    bairroNome: '',
    cep: '',
    cidadeSolicitante: '',
    pontoReferencia: '',
    tipoArvore: '',
    quantidadeArvores: 1,
    porte: '' as 'Pequeno' | 'Médio' | 'Grande' | '',
    coordenadas: '',
    estadoArvore: '' as EstadoArvore | '',
    seraSubstituida: '' as 'sim' | 'nao' | '',
    qualArvoreReplantio: '',
    localReplantio: '' as 'mesmo' | 'outro' | '',
    ondeReplantio: '',
    motivoNaoSubstituicao: '',
    relatoProblema: '',
    termoAceito: false,
  })

  useEffect(() => {
    if (editData) {
      setForm({
        nomeCompleto: editData.nomeCompleto || '',
        cpf: editData.cpf || '',
        telefone: editData.telefone || '',
        ruaNome: editData.ruaNome || '',
        numero: editData.numero || '',
        bairroNome: editData.bairroNome || '',
        cep: editData.cep || '',
        cidadeSolicitante: editData.cidadeSolicitante || '',
        pontoReferencia: editData.pontoReferencia || '',
        tipoArvore: editData.tipoArvore || '',
        quantidadeArvores: editData.quantidadeArvores || 1,
        porte: editData.porte || '',
        coordenadas: editData.coordenadas || '',
        estadoArvore: editData.estadoArvore || '',
        seraSubstituida: editData.seraSubstituida ? 'sim' : (editData.seraSubstituida === false ? 'nao' : ''),
        qualArvoreReplantio: editData.qualArvoreReplantio || '',
        localReplantio: editData.localReplantio || '',
        ondeReplantio: editData.ondeReplantio || '',
        motivoNaoSubstituicao: editData.motivoNaoSubstituicao || '',
        relatoProblema: editData.relatoProblema || '',
        termoAceito: editData.termoAceito || false,
      })
      if (editData.fotos) setFotos(editData.fotos)
    }
  }, [])

  const update = (f: string, value: any) => setForm(prev => ({ ...prev, [f]: value }))

  // ─── Verifica se CPF já tem conta no portal ───────────────────────────────
  const verificarContaCidadao = async (cpf: string) => {
    const cpfLimpo = normalizarCPF(cpf)
    if (cpfLimpo.length < 11) { setEstadoCidadao('idle'); return }
    setEstadoCidadao('verificando')
    setErroCidadao('')
    try {
      const { list } = await lumi.entities.cidadaos_portal.list({
        filter: { cpf: cpfLimpo },
        limit: 1,
      })
      setEstadoCidadao(list && list.length > 0 ? 'ja_tem_acesso' : 'sem_acesso')
    } catch {
      setEstadoCidadao('idle')
    }
  }

  // ─── Cria conta cidadão a partir dos dados da solicitação ────────────────
  const handleCriarAcessoCidadao = async () => {
    const cpfLimpo = normalizarCPF(form.cpf)
    if (!cpfLimpo || !form.nomeCompleto.trim()) {
      toast.error('Nome e CPF são obrigatórios para criar o acesso.')
      return
    }
    setEstadoCidadao('criando')
    setErroCidadao('')
    try {
      // Dupla verificação de existência
      const { list } = await lumi.entities.cidadaos_portal.list({
        filter: { cpf: cpfLimpo },
        limit: 1,
      })
      if (list && list.length > 0) {
        setEstadoCidadao('ja_tem_acesso')
        toast('Este CPF já possui acesso ao Portal do Cidadão.', { icon: 'ℹ️' })
        return
      }
      const now = new Date().toISOString()
      const senhaInicial = senhaDosCPF(cpfLimpo)
      // 1. Cria em cidadaos_portal
      //    senhaCustom = senha inicial visível; sem senhaCustom o login usa os 6 primeiros dígitos do CPF
      await lumi.entities.cidadaos_portal.create({
        cpf: cpfLimpo,
        nomeCompleto: form.nomeCompleto.trim(),
        ativo: true,
        // Não gravamos senhaCustom aqui — a senha padrão é derivada do CPF no loginCidadao
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      })
      // 2. Cria em usuarios_sistema para aparecer na listagem de Usuários
      //    campo 'senha' é obrigatório no schema; 'senhaAtual' é o campo visível para o admin
      await lumi.entities.usuarios_sistema.create({
        userId: `cidadao_${cpfLimpo}`,
        userName: form.nomeCompleto.trim(),
        email: `${cpfLimpo}@cidadao.geoa`,
        cargo: 'Portal do Cidadão',
        userRole: 'CIDADAO',
        ativo: true,
        senha: senhaInicial,
        senhaAtual: senhaInicial,
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      })
      setEstadoCidadao('criado')
      toast.success('Acesso ao Portal do Cidadão criado com sucesso!')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setErroCidadao(msg)
      setEstadoCidadao('erro')
      toast.error('Erro ao criar acesso. Tente novamente.')
    }
  }

  // ─── Busca CEP via ViaCEP ─────────────────────────────────────────────────
  const handleCEPBlur = async (cep: string) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    setBuscandoCEP(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) {
        const cidade = data.localidade ? `${data.localidade}/${data.uf}` : ''
        setForm(prev => ({ ...prev, cidadeSolicitante: cidade }))
      } else {
        toast.error('CEP não encontrado.')
      }
    } catch {
      toast.error('Erro ao buscar CEP.')
    } finally {
      setBuscandoCEP(false)
    }
  }

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxSize = 1024
        let w = img.width, h = img.height
        if (w > maxSize || h > maxSize) {
          if (w > h) { h = Math.round((h / w) * maxSize); w = maxSize }
          else { w = Math.round((w / h) * maxSize); h = maxSize }
        }
        canvas.width = w; canvas.height = h
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
        URL.revokeObjectURL(objectUrl)
        canvas.toBlob((blob) => {
          if (blob) resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
          else resolve(file)
        }, 'image/jpeg', 0.78)
      }
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file) }
      img.src = objectUrl
    })
  }

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    if (fotos.length + files.length > 6) { toast.error('Máximo de 6 fotos permitidas.'); return }
    setUploadingFoto(true)
    try {
      const validFiles: File[] = []
      for (const file of files) {
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
          toast.error(`Formato inválido: ${file.name}`); continue
        }
        validFiles.push(await compressImage(file))
      }
      if (validFiles.length === 0) return
      const results = await lumi.tools.file.upload(validFiles)
      const urls: string[] = []
      for (const r of results) {
        if ((r as any).fileUrl) urls.push((r as any).fileUrl)
        else if ((r as any).uploadError) toast.error(`Erro: ${(r as any).uploadError}`)
      }
      if (urls.length > 0) { setFotos(prev => [...prev, ...urls]); toast.success(`${urls.length} foto(s) adicionada(s)!`) }
    } catch (err: any) {
      console.error('Erro upload fotos:', err)
      toast.error('Erro ao enviar foto. Tente novamente.')
    } finally {
      setUploadingFoto(false)
      if (fotoInputRef.current) fotoInputRef.current.value = ''
    }
  }

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!form.nomeCompleto.trim()) { toast.error('Informe o nome completo.'); return false }
      if (!form.cpf.trim()) { toast.error('Informe o CPF.'); return false }
      if (!form.telefone.trim()) { toast.error('Informe o telefone.'); return false }
      if (!form.ruaNome.trim()) { toast.error('Informe a rua.'); return false }
      if (!form.numero.trim()) { toast.error('Informe o número.'); return false }
      if (!form.bairroNome.trim()) { toast.error('Informe o bairro.'); return false }
    }
    if (step === 1) {
      if (!form.tipoArvore.trim()) { toast.error('Informe o tipo da árvore.'); return false }
      if (!form.porte) { toast.error('Selecione o porte da árvore.'); return false }
      if (!form.estadoArvore) { toast.error('Selecione o estado da árvore.'); return false }
      if (!form.seraSubstituida) { toast.error('Informe se a árvore será substituída.'); return false }
      if (form.seraSubstituida === 'sim') {
        if (!form.qualArvoreReplantio.trim()) { toast.error('Informe qual árvore será plantada.'); return false }
        if (!form.localReplantio) { toast.error('Informe o local de replantio.'); return false }
        if (form.localReplantio === 'outro' && !form.ondeReplantio.trim()) { toast.error('Informe onde será o replantio.'); return false }
      }
      if (form.seraSubstituida === 'nao' && !form.motivoNaoSubstituicao.trim()) {
        toast.error('Informe o motivo de não substituir a árvore.'); return false
      }
      if (!form.relatoProblema.trim()) { toast.error('Informe a causa do problema.'); return false }
    }
    if (step === 2) {
      if (!form.termoAceito) { toast.error('Você deve aceitar o termo de responsabilidade.'); return false }
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep()) return
    setStep(prev => Math.min(prev + 1, STEPS.length - 1))
    // Ao chegar no step 3 (Confirmar), verifica CPF automaticamente
    if (step === 2 && form.cpf) {
      verificarContaCidadao(form.cpf)
    }
  }
  const handleBack = () => setStep(prev => Math.max(prev - 1, 0))

  const handleSave = async () => {
    if (!validateStep()) return
    setSaving(true)
    try {
      const solRes = await lumi.entities.solicitacoes.list({ limit: 9999 })
      const total = solRes.list?.length || 0
      const protocolo = editData?.protocolo || gerarProtocolo(total)
      const now = new Date().toISOString()

      const payload = {
        protocolo,
        nomeCompleto: form.nomeCompleto,
        cpf: form.cpf,
        telefone: form.telefone,
        cep: form.cep || undefined,
        cidadeSolicitante: form.cidadeSolicitante || undefined,
        bairroId: '',
        bairroNome: form.bairroNome,
        ruaId: '',
        ruaNome: form.ruaNome,
        numero: form.numero,
        pontoReferencia: form.pontoReferencia || undefined,
        tipoArvore: form.tipoArvore,
        quantidadeArvores: Number(form.quantidadeArvores),
        porte: form.porte,
        coordenadas: form.coordenadas || undefined,
        estadoArvore: form.estadoArvore as EstadoArvore,
        seraSubstituida: form.seraSubstituida === 'sim',
        qualArvoreReplantio: form.seraSubstituida === 'sim' ? form.qualArvoreReplantio : undefined,
        localReplantio: form.seraSubstituida === 'sim' ? (form.localReplantio || undefined) : undefined,
        ondeReplantio: form.seraSubstituida === 'sim' && form.localReplantio === 'outro' ? form.ondeReplantio : undefined,
        motivoNaoSubstituicao: form.seraSubstituida === 'nao' ? form.motivoNaoSubstituicao : undefined,
        relatoProblema: form.relatoProblema,
        fotos: fotos.length > 0 ? fotos : undefined,
        documentos: undefined,
        termoAceito: form.termoAceito,
        assinaturasSolicitante: { nome: form.nomeCompleto, cpf: form.cpf },
        // Status inicial: já entra como Em Análise
        status: editData?.status || 'Em Análise',
        creator: 'sistema',
        createdAt: editData?.createdAt || now,
        updatedAt: now,
      }

      if (editData?._id) {
        await lumi.entities.solicitacoes.update(editData._id, payload)
      } else {
        const result = await lumi.entities.solicitacoes.create(payload) as any
        // Registra os dois status iniciais no histórico
        await lumi.entities.historico_status.create({
          solicitacaoId: result._id, protocolo,
          status: 'Solicitação Recebida', responsavel: 'Sistema',
          observacoes: 'Solicitação recebida e registada no sistema.',
          creator: 'sistema', createdAt: now, updatedAt: now,
        })
        await lumi.entities.historico_status.create({
          solicitacaoId: result._id, protocolo,
          status: 'Em Análise', responsavel: 'Sistema',
          observacoes: 'Processo encaminhado para análise técnica.',
          creator: 'sistema', createdAt: now, updatedAt: now,
        })
      }

      toast.success(editData ? 'Solicitação atualizada!' : 'Solicitação criada com sucesso!')
      if (onSaved) { onSaved() } else { navigate('/solicitacoes') }
    } catch (err) {
      console.error(err)
      toast.error('Erro ao salvar solicitação.')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f3b50] focus:border-transparent transition'
  const selectCls = `${inputCls} bg-white`
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5'

  // ─── Bloco de acesso ao portal (Step 3) ──────────────────────────────────
  const cpfLimpo = normalizarCPF(form.cpf)
  const senhaPortal = senhaDosCPF(form.cpf)

  const renderBlocoAcesso = () => {
    if (!form.cpf || cpfLimpo.length < 11) return null

    // Já tem acesso
    if (estadoCidadao === 'ja_tem_acesso') {
      return (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
          <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Cidadão já possui acesso ao Portal</p>
            <p className="text-xs text-emerald-700 mt-0.5">O CPF <strong>{form.cpf}</strong> já tem conta criada no Portal do Cidadão.</p>
          </div>
        </div>
      )
    }

    // Criado com sucesso agora
    if (estadoCidadao === 'criado') {
      return (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <p className="text-sm font-bold text-emerald-800">Acesso ao Portal criado com sucesso!</p>
          </div>
          <p className="text-xs text-emerald-700">O cidadão já pode aceder ao Portal com as seguintes credenciais:</p>
          <div className="flex gap-3 mt-2">
            <div className="flex-1 bg-white rounded-xl border border-emerald-200 px-3 py-2 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Login (CPF)</p>
              <p className="text-sm font-bold text-[#0f3b50] font-mono">{cpfLimpo}</p>
            </div>
            <div className="flex-1 bg-white rounded-xl border border-emerald-200 px-3 py-2 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Senha</p>
              <p className="text-sm font-bold text-[#0f3b50] font-mono">{senhaPortal}</p>
            </div>
          </div>
        </div>
      )
    }

    // Erro ao criar
    if (estadoCidadao === 'erro') {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl space-y-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <p className="text-sm font-semibold text-red-800">Erro ao criar acesso</p>
          </div>
          <p className="text-xs text-red-700">{erroCidadao || 'Ocorreu um erro inesperado.'}</p>
          <button
            type="button"
            onClick={handleCriarAcessoCidadao}
            className="mt-1 flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-xl transition"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Tentar Novamente
          </button>
        </div>
      )
    }

    // Sem acesso — exibe botão para criar
    if (estadoCidadao === 'sem_acesso') {
      return (
        <div className="p-4 bg-[#e8f1f5] border border-[#b8d4e0] rounded-2xl space-y-3">
          <div className="flex items-start gap-3">
            <UserPlus className="w-5 h-5 text-[#0f3b50] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-[#0f3b50]">Criar Acesso ao Portal do Cidadão</p>
              <p className="text-xs text-[#2a5f76] mt-0.5">
                Este CPF ainda não tem conta no Portal. Crie o acesso para que o cidadão possa acompanhar os seus processos online.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1 bg-white rounded-xl border border-[#b8d4e0] px-3 py-2 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Login (CPF)</p>
              <p className="text-sm font-bold text-[#0f3b50] font-mono">{cpfLimpo}</p>
            </div>
            <div className="flex-1 bg-white rounded-xl border border-[#b8d4e0] px-3 py-2 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Senha</p>
              <p className="text-sm font-bold text-[#0f3b50] font-mono">{senhaPortal}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCriarAcessoCidadao}
            disabled={estadoCidadao === 'criando'}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0f3b50] hover:bg-[#0a2d3d] disabled:opacity-60 text-white text-sm font-semibold rounded-2xl transition"
          >
            <UserPlus className="w-4 h-4" />
            Criar Acesso ao Portal
          </button>
        </div>
      )
    }

    // Verificando
    if (estadoCidadao === 'verificando' || estadoCidadao === 'criando') {
      return (
        <div className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-2xl">
          <Loader2 className="w-4 h-4 text-[#0f3b50] animate-spin" />
          <p className="text-sm text-gray-600">
            {estadoCidadao === 'verificando' ? 'Verificando conta no portal...' : 'Criando acesso...'}
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => onSaved ? onSaved() : navigate('/solicitacoes')}
          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {editData ? 'Editar Solicitação' : 'Nova Solicitação'}
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">Remoção de Árvore – Prefeitura de São José do Seridó/RN</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-3xl border border-gray-100 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between overflow-x-auto gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-shrink-0">
              <div className={`flex items-center gap-1.5 ${i <= step ? '' : 'opacity-40'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition
                  ${i < step ? 'bg-[#0f3b50] text-white' : i === step ? 'bg-[#0f3b50] text-white ring-4 ring-[#e8f1f5]' : 'bg-gray-100 text-gray-500'}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${i === step ? 'text-gray-900' : 'text-gray-400'}`}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-4 sm:w-8 h-0.5 rounded ${i < step ? 'bg-[#0f3b50]' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">

        {/* ── Step 0: Solicitante ── */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Dados do Solicitante</h2>
              <p className="text-xs text-gray-400 mt-0.5">Preencha os dados de quem solicita a remoção</p>
            </div>

            {/* ── Identificação ── */}
            <div>
              <p className="text-xs font-semibold text-[#0f3b50] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> Identificação
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Nome Completo <span className="text-red-500">*</span></label>
                  <input value={form.nomeCompleto} onChange={e => update('nomeCompleto', e.target.value)}
                    placeholder="Nome completo do solicitante" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>CPF <span className="text-red-500">*</span></label>
                  <input
                    value={form.cpf}
                    onChange={e => update('cpf', formatarCPF(e.target.value))}
                    onBlur={e => {
                      // Reseta estado ao mudar CPF
                      if (normalizarCPF(e.target.value).length >= 11) {
                        setEstadoCidadao('idle')
                      }
                    }}
                    placeholder="000.000.000-00"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefone <span className="text-red-500">*</span></label>
                  <input value={form.telefone} onChange={e => update('telefone', formatarTelefone(e.target.value))}
                    placeholder="(84) 99999-9999" className={inputCls} />
                </div>
              </div>
            </div>

            {/* ── Endereço ── */}
            <div>
              <p className="text-xs font-semibold text-[#0f3b50] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Endereço
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Linha 1: Rua (largo) + Número (curto) */}
                <div className="sm:col-span-1">
                  <label className={labelCls}>Rua <span className="text-red-500">*</span></label>
                  <input value={form.ruaNome} onChange={e => update('ruaNome', e.target.value)}
                    placeholder="Nome da rua" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Número <span className="text-red-500">*</span></label>
                  <input value={form.numero} onChange={e => update('numero', e.target.value)}
                    placeholder="Nº" className={inputCls} />
                </div>

                {/* Linha 2: Bairro */}
                <div className="sm:col-span-2">
                  <label className={labelCls}>Bairro <span className="text-red-500">*</span></label>
                  <input value={form.bairroNome} onChange={e => update('bairroNome', e.target.value)}
                    placeholder="Nome do bairro" className={inputCls} />
                </div>

                {/* Linha 3: CEP + Cidade */}
                <div>
                  <label className={labelCls}>CEP</label>
                  <div className="relative">
                    <input
                      value={form.cep}
                      onChange={e => update('cep', formatarCEP(e.target.value))}
                      onBlur={e => handleCEPBlur(e.target.value)}
                      placeholder="00000-000"
                      className={inputCls}
                      maxLength={9}
                    />
                    {buscandoCEP && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0f3b50] animate-spin" />
                    )}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Cidade</label>
                  <div className="relative">
                    <input
                      value={form.cidadeSolicitante}
                      onChange={e => update('cidadeSolicitante', e.target.value)}
                      placeholder="Preenchido pelo CEP"
                      className={`${inputCls} pr-9`}
                    />
                    {form.cidadeSolicitante && (
                      <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0f3b50]" />
                    )}
                  </div>
                </div>

                {/* Linha 4: Ponto de referência (largo) */}
                <div className="sm:col-span-2">
                  <label className={labelCls}>Ponto de Referência</label>
                  <input value={form.pontoReferencia} onChange={e => update('pontoReferencia', e.target.value)}
                    placeholder="Ex: Próximo à escola, em frente à praça..." className={inputCls} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Árvore + Fotos ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Dados da Árvore & Fotos</h2>
              <p className="text-xs text-gray-400 mt-0.5">Informações sobre a(s) árvore(s) e fotos do local</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Tipo da Árvore <span className="text-red-500">*</span></label>
                <input value={form.tipoArvore} onChange={e => update('tipoArvore', e.target.value)}
                  placeholder="Ex: Mangueira, Cajueiro..." className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Quantidade <span className="text-red-500">*</span></label>
                <input type="number" min={1} value={form.quantidadeArvores}
                  onChange={e => update('quantidadeArvores', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Estado da Árvore <span className="text-red-500">*</span></label>
                <select value={form.estadoArvore} onChange={e => update('estadoArvore', e.target.value)} className={selectCls}>
                  <option value="">Selecione</option>
                  {ESTADOS_ARVORE.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            </div>

            {/* Porte */}
            <div className="space-y-2">
              <label className={labelCls}>Porte da Árvore <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {(['Pequeno', 'Médio', 'Grande'] as const).map(opt => (
                  <button key={opt} type="button"
                    onClick={() => update('porte', opt)}
                    className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition
                      ${form.porte === opt
                        ? 'bg-[#0f3b50] text-white border-[#0f3b50]'
                        : 'border-gray-200 text-gray-600 hover:border-[#b8d4e0] hover:bg-[#e8f1f5]'}`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Coordenadas (opcional) */}
            <div>
              <label className={labelCls}>Coordenadas GPS <span className="text-gray-400 font-normal normal-case">(opcional)</span></label>
              <input value={form.coordenadas} onChange={e => update('coordenadas', e.target.value)}
                placeholder="Ex: -6.8921, -36.8745" className={inputCls} />
            </div>

            <div className="space-y-3">
              <label className={labelCls}>A árvore será substituída? <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {(['sim', 'nao'] as const).map(opt => (
                  <button key={opt} type="button"
                    onClick={() => {
                      update('seraSubstituida', opt)
                      if (opt === 'sim') {
                        // Pré-preenche com os dados da árvore a ser removida e o endereço da solicitação
                        if (!form.qualArvoreReplantio && form.tipoArvore) {
                          update('qualArvoreReplantio', form.tipoArvore)
                        }
                        if (!form.localReplantio) {
                          update('localReplantio', 'mesmo')
                        }
                      }
                    }}
                    className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition
                      ${form.seraSubstituida === opt
                        ? 'bg-[#0f3b50] text-white border-[#0f3b50]'
                        : 'border-gray-200 text-gray-600 hover:border-[#b8d4e0] hover:bg-[#e8f1f5]'}`}>
                    {opt === 'sim' ? 'Sim' : 'Não'}
                  </button>
                ))}
              </div>
            </div>

            {form.seraSubstituida === 'sim' && (
              <div className="space-y-4 p-4 bg-[#e8f1f5] rounded-2xl border border-[#b8d4e0]">
                <div className="flex items-start gap-2 bg-white/70 border border-[#b8d4e0] rounded-xl px-3 py-2">
                  <span className="text-[10px] text-[#0f3b50] font-semibold uppercase tracking-wide mt-0.5">ℹ</span>
                  <p className="text-xs text-[#2a5f76] leading-relaxed">
                    Os campos abaixo foram pré-preenchidos com os dados da árvore a ser removida e o endereço da solicitação. Ajuste conforme necessário.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Qual árvore será plantada no local? <span className="text-red-500">*</span></label>
                  <input value={form.qualArvoreReplantio} onChange={e => update('qualArvoreReplantio', e.target.value)}
                    placeholder="Ex: Ipê-amarelo, Angico, Aroeira..." className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Local do Replantio <span className="text-red-500">*</span></label>
                  <div className="flex gap-3">
                    {(['mesmo', 'outro'] as const).map(opt => (
                      <button key={opt} type="button" onClick={() => {
                        update('localReplantio', opt)
                        if (opt === 'outro' && !form.ondeReplantio) {
                          // Pré-preenche o endereço com os dados da solicitação
                          const endSol = [form.ruaNome, form.numero ? `nº ${form.numero}` : '', form.bairroNome].filter(Boolean).join(', ')
                          if (endSol) update('ondeReplantio', endSol)
                        }
                      }}
                        className={`flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition
                          ${form.localReplantio === opt
                            ? 'bg-[#0f3b50] text-white border-[#0f3b50]'
                            : 'border-[#b8d4e0] text-[#0f3b50] hover:bg-white'}`}>
                        {opt === 'mesmo' ? 'Mesmo Local' : 'Outro Local'}
                      </button>
                    ))}
                  </div>
                </div>
                {form.localReplantio === 'outro' && (
                  <div>
                    <label className={labelCls}>Onde será realizado o replantio? <span className="text-red-500">*</span></label>
                    <textarea value={form.ondeReplantio} onChange={e => update('ondeReplantio', e.target.value)}
                      placeholder="Descreva o local onde será realizado o replantio..." rows={2}
                      className={`${inputCls} resize-none`} />
                  </div>
                )}
              </div>
            )}

            {form.seraSubstituida === 'nao' && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 space-y-3">
                  <div>
                    <label className={labelCls}>Motivo da não substituição <span className="text-red-500">*</span></label>
                    <textarea value={form.motivoNaoSubstituicao} onChange={e => update('motivoNaoSubstituicao', e.target.value)}
                      placeholder="Descreva o motivo pelo qual a árvore não será substituída..." rows={3}
                      className={`${inputCls} resize-none border-amber-300 focus:ring-amber-500`} />
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-red-800">Atenção — Possível incidência de taxa</p>
                    <p className="text-xs text-red-700 leading-relaxed">
                      O não plantio de uma árvore para substituir uma árvore removida pode gerar a cobrança de uma
                      <strong> taxa de compensação ambiental</strong>, conforme previsto na legislação municipal.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Causa do Problema Relatada pelo Solicitante <span className="text-red-500">*</span></label>
              <textarea value={form.relatoProblema} onChange={e => update('relatoProblema', e.target.value)}
                placeholder="Descreva detalhadamente o problema, risco de queda, danos causados..." rows={4}
                className={`${inputCls} resize-none`} />
            </div>

            {/* Fotos */}
            <div className="pt-2 border-t border-gray-100 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fotos do Local</p>
                <p className="text-xs text-gray-400 mt-0.5">Até 6 fotos — JPG, PNG ou WEBP (compressão automática)</p>
              </div>
              <input ref={fotoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple onChange={handleFotoUpload} className="hidden" />
              {fotos.length < 6 && (
                <button type="button" onClick={() => fotoInputRef.current?.click()} disabled={uploadingFoto}
                  className="flex flex-col items-center justify-center gap-2 w-full py-6 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-[#0f3b50] hover:bg-[#e8f1f5] hover:text-[#0f3b50] transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {uploadingFoto ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm font-medium">Enviando e comprimindo...</span></>
                  ) : (
                    <><Upload className="w-5 h-5" /><span className="text-sm font-medium">Clique para adicionar fotos</span>
                    <span className="text-xs text-gray-400">{fotos.length}/6 fotos adicionadas</span></>
                  )}
                </button>
              )}
              {fotos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {fotos.map((url, i) => (
                    <div key={i} className="relative group rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                      <img src={url} alt={`Foto ${i + 1}`}
                        className="w-full h-28 object-cover transition group-hover:brightness-90" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <button type="button" onClick={() => setFotos(prev => prev.filter((_, idx) => idx !== i))}
                          className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 py-1">
                        <span className="text-xs text-white font-medium px-2">Foto {i + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {fotos.length === 0 && (
                <p className="text-xs text-gray-400 text-center">Nenhuma foto adicionada. As fotos são opcionais mas ajudam na análise.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Termo ── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Declaração do Solicitante</h2>
              <p className="text-xs text-gray-400 mt-0.5">Leia atentamente e confirme o aceite</p>
            </div>
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 text-sm text-gray-700 leading-relaxed space-y-3">
              <p>Declaro que todas as informações prestadas nesta solicitação são verdadeiras e estão em conformidade com a <strong>Lei Municipal nº 426, de 21 de novembro de 2018</strong>.</p>
              <p>Estou ciente de que informações falsas ou procedimentos realizados sem autorização estarão sujeitos às penalidades previstas na legislação municipal.</p>
              <p className="font-medium text-gray-800">Art. 22. Em casos de realização de podas, cortes ou remoções não autorizadas, ficam os infratores passíveis das penalidades estabelecidas na presente Lei, como advertência em caso do munícipe ser primário e, se reincidente, multa variando de 10% a 100% do salário mínimo vigente, de acordo com a reincidência.</p>
            </div>
            <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-2xl border-2 transition ${form.termoAceito ? 'border-[#0f3b50] bg-[#e8f1f5]' : 'border-gray-200 hover:border-[#b8d4e0]'}`}>
              <input type="checkbox" checked={form.termoAceito}
                onChange={e => update('termoAceito', e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#0f3b50] flex-shrink-0" />
              <span className="text-sm text-gray-700 font-medium">
                Li e concordo com os termos acima e declaro que as informações são verdadeiras.
              </span>
            </label>
            {!form.termoAceito && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-medium">Você precisa aceitar o termo para finalizar a solicitação.</span>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Confirmar + Acesso ao Portal ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Confirmar & Finalizar</h2>
              <p className="text-xs text-gray-400 mt-0.5">Revise os dados, gere o acesso ao portal e finalize</p>
            </div>

            {/* Resumo */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo da Solicitação</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Solicitante:</span> <span className="font-medium text-gray-800">{form.nomeCompleto || '—'}</span></div>
                <div><span className="text-gray-500">CPF:</span> <span className="font-medium text-gray-800">{form.cpf || '—'}</span></div>
                <div><span className="text-gray-500">Árvore:</span> <span className="font-medium text-gray-800">{form.tipoArvore || '—'}</span></div>
                <div><span className="text-gray-500">Bairro:</span> <span className="font-medium text-gray-800">{form.bairroNome || '—'}</span></div>
                <div><span className="text-gray-500">Rua:</span> <span className="font-medium text-gray-800">{form.ruaNome || '—'}{form.numero ? `, nº ${form.numero}` : ''}</span></div>
                <div><span className="text-gray-500">Substituição:</span> <span className={`font-medium ${form.seraSubstituida === 'sim' ? 'text-[#0f3b50]' : 'text-red-600'}`}>{form.seraSubstituida === 'sim' ? 'Sim' : form.seraSubstituida === 'nao' ? 'Não' : '—'}</span></div>
                {form.seraSubstituida === 'sim' && form.qualArvoreReplantio && (
                  <div><span className="text-gray-500">Plantio:</span> <span className="font-medium text-gray-800">{form.qualArvoreReplantio}</span></div>
                )}
              </div>
            </div>

            {/* Aviso de taxa */}
            {form.seraSubstituida === 'nao' && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Aviso — Taxa de Compensação Ambiental</p>
                  <p className="text-xs text-red-700 mt-1 leading-relaxed">
                    O não plantio de uma árvore substituta pode resultar na cobrança de uma <strong>taxa de compensação ambiental</strong> prevista na legislação municipal.
                  </p>
                </div>
              </div>
            )}

            {/* ── Acesso ao Portal do Cidadão ── */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Acesso ao Portal do Cidadão
              </p>
              {renderBlocoAcesso()}
              {estadoCidadao === 'idle' && cpfLimpo.length >= 11 && (
                <button
                  type="button"
                  onClick={() => verificarContaCidadao(form.cpf)}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-[#0f3b50] border border-[#b8d4e0] rounded-xl hover:bg-[#e8f1f5] transition"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Verificar acesso ao portal
                </button>
              )}
            </div>

            {/* Assinaturas físicas */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Campos para Assinatura Física</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-full h-16 border-b-2 border-gray-400" />
                  <div className="text-center">
                    <p className="text-xs font-bold text-gray-800 leading-tight">{form.nomeCompleto || 'Solicitante'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">CPF: {form.cpf || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Solicitante</p>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="w-full h-16 border-b-2 border-gray-400" />
                  <div className="text-center">
                    <p className="text-xs font-bold text-gray-800">Responsável Técnico</p>
                    <p className="text-xs text-gray-500 mt-0.5">Secretaria Municipal de Meio Ambiente</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>Atenção:</strong> As assinaturas físicas deverão ser apostas manualmente no documento impresso. Clique em "Finalizar" para registar a solicitação e gerar o protocolo.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navegação */}
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={handleBack} disabled={step === 0}
          className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 disabled:opacity-30 transition">
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </button>

        {step < STEPS.length - 1 ? (
          <button type="button" onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#0f3b50] hover:bg-[#0a2d3d] text-white text-sm font-semibold rounded-2xl transition shadow-sm">
            Próximo
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#0f3b50] hover:bg-[#0a2d3d] disabled:bg-[#7a9fae] text-white text-sm font-semibold rounded-2xl transition shadow-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <TreePine className="w-4 h-4" />}
            {saving ? 'Salvando...' : 'Finalizar Solicitação'}
          </button>
        )}
      </div>
    </div>
  )
}
